import { settings } from '../core/settings';

/** Procedural sound effects with Web Audio. Everything is synthesised; no assets. */
let actx: AudioContext | null = null;
let master: GainNode | null = null;

export function unlockAudio(): void {
  if (!actx) {
    const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctor) return;
    actx = new Ctor();
    master = actx.createGain();
    master.gain.value = settings.sound ? 0.9 : 0;
    master.connect(actx.destination);
  }
  if (actx.state === 'suspended') void actx.resume();
}

/** Silence everything at once (also notes already scheduled, like a radio song). */
export function applyMute(): void {
  if (master && actx) master.gain.setTargetAtTime(settings.sound ? 0.9 : 0, actx.currentTime, 0.02);
}

function ready(): AudioContext | null {
  if (!settings.sound) return null;
  unlockAudio();
  return actx;
}

/** One enveloped oscillator note. `at` is seconds from now. */
export function tone(freq: number, dur: number, type: OscillatorType = 'sine', vol = 0.08, at = 0, slideTo?: number): void {
  const ctx = ready();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime + at;
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, t0);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(vol, t0 + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  o.connect(g).connect(master);
  o.start(t0);
  o.stop(t0 + dur + 0.02);
}

/** The live context and master bus, only once audio has been unlocked by a gesture (for looping ambience). */
export function audioOut(): { ctx: AudioContext; out: GainNode } | null {
  return actx && master ? { ctx: actx, out: master } : null;
}

/** Footstep flavour picked by the ambience (echoing school corridor, splashy wet ground). */
export type StepMode = 'normal' | 'hall' | 'wet';
let stepMode: () => StepMode = () => 'normal';
export function setStepMode(fn: () => StepMode): void {
  stepMode = fn;
}

/**
 * Filtered noise burst with a gain envelope given as [time, gain] points
 * (thunder rumbles, quake rattles, wet steps).
 */
function noiseEnv(dur: number, type: BiquadFilterType, freq: number, env: [number, number][], at = 0, q = 0.8): void {
  const ctx = ready();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime + at;
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  // Brown-ish noise: deeper and softer than white noise.
  let last = 0;
  for (let i = 0; i < len; i++) {
    last = (last + 0.06 * (Math.random() * 2 - 1)) / 1.06;
    data[i] = type === 'lowpass' ? last * 3.2 : Math.random() * 2 - 1;
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  for (const [t, v] of env) g.gain.linearRampToValueAtTime(Math.max(0.0001, v), t0 + t);
  src.connect(f).connect(g).connect(master);
  src.start(t0);
  src.stop(t0 + dur + 0.05);
}

/** A struck chime: fundamental plus soft inharmonic partials, long decay. */
function chime(freq: number, at: number, vol = 0.06, decay = 1.6): void {
  tone(freq, decay, 'sine', vol, at);
  tone(freq * 2.0, decay * 0.6, 'sine', vol * 0.28, at);
  tone(freq * 3.01, decay * 0.35, 'triangle', vol * 0.08, at);
}

function noise(dur: number, vol: number, filterFreq: number, at = 0): void {
  const ctx = ready();
  if (!ctx || !master) return;
  const t0 = ctx.currentTime + at;
  const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const f = ctx.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = filterFreq;
  const g = ctx.createGain();
  g.gain.value = vol;
  src.connect(f).connect(g).connect(master);
  src.start(t0);
}

export type Sfx =
  | 'step'
  | 'click'
  | 'ok'
  | 'fanfare'
  | 'bad'
  | 'water'
  | 'brew'
  | 'flush'
  | 'ring'
  | 'pop'
  | 'drop'
  | 'line'
  | 'honk'
  | 'flap'
  | 'door'
  | 'pickup'
  | 'rustle'
  | 'hop'
  // phase D (bodies refined by the sky package)
  | 'bell'
  | 'checkout'
  | 'award'
  | 'thunder'
  | 'quake'
  | 'stepHall'
  // sky package extras
  | 'stepWet'
  | 'umbrella'
  | 'alarm';

export function sfx(name: Sfx): void {
  switch (name) {
    case 'step': {
      const mode = stepMode();
      if (mode === 'hall') return sfx('stepHall');
      if (mode === 'wet') return sfx('stepWet');
      noise(0.04, 0.05, 900);
      break;
    }
    case 'click':
      tone(880, 0.05, 'square', 0.025);
      break;
    case 'pop':
      tone(520, 0.08, 'sine', 0.06, 0, 900);
      break;
    case 'ok':
      [523.25, 659.25, 783.99].forEach((f, i) => tone(f, 0.16, 'triangle', 0.05, i * 0.06));
      break;
    case 'fanfare':
      [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => tone(f, 0.22, 'triangle', 0.06, i * 0.07));
      tone(261.6, 0.6, 'sine', 0.05, 0.28);
      break;
    case 'bad':
      tone(330, 0.18, 'sawtooth', 0.04, 0, 220);
      tone(220, 0.3, 'sawtooth', 0.04, 0.16, 150);
      break;
    case 'water':
      tone(320, 0.16, 'sine', 0.07, 0, 620);
      tone(500, 0.12, 'sine', 0.04, 0.08, 280);
      break;
    case 'brew':
      for (let i = 0; i < 6; i++) noise(0.05, 0.05, 1600, i * 0.05);
      break;
    case 'flush':
      noise(0.7, 0.12, 700);
      tone(160, 0.5, 'triangle', 0.04, 0.1, 70);
      break;
    case 'ring':
      for (let i = 0; i < 3; i++) {
        tone(440, 0.12, 'sine', 0.07, i * 0.25);
        tone(480, 0.12, 'sine', 0.07, i * 0.25);
      }
      break;
    case 'drop':
      tone(200, 0.08, 'square', 0.04, 0, 120);
      break;
    case 'honk':
      tone(392, 0.12, 'square', 0.035);
      tone(330, 0.16, 'square', 0.035, 0.14);
      break;
    case 'flap':
      for (let i = 0; i < 4; i++) noise(0.03, 0.04, 2400, i * 0.05);
      break;
    case 'door':
      noise(0.08, 0.06, 500);
      tone(180, 0.12, 'triangle', 0.04, 0.05, 140);
      break;
    case 'pickup':
      [784, 1046.5, 1568].forEach((f, i) => tone(f, 0.12, 'sine', 0.05, i * 0.05));
      break;
    case 'bell': {
      // The Westminster chimes every Taiwanese school plays: E C D G · G D E C.
      const E = 659.25, C = 523.25, D = 587.33, G = 392;
      [E, C, D, G, G, D, E, C].forEach((f, i) => chime(f, i * 0.62 + (i >= 4 ? 0.5 : 0), 0.055, i === 3 || i === 7 ? 2.4 : 1.5));
      break;
    }
    case 'checkout':
      // Barcode beep, then the drawer and a bright till "ka-ching".
      tone(2350, 0.07, 'square', 0.025);
      noiseEnv(0.18, 'bandpass', 900, [[0.01, 0.06], [0.18, 0]], 0.16, 1.2);
      chime(2093, 0.3, 0.035, 0.5);
      chime(2637, 0.36, 0.03, 0.7);
      break;
    case 'award':
      [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(f, 0.28, 'triangle', 0.065, i * 0.09));
      [261.63, 329.63, 392].forEach((f) => tone(f, 0.9, 'sine', 0.035, 0.36));
      [1568, 2093, 2637].forEach((f, i) => chime(f, 0.42 + i * 0.07, 0.018, 0.6));
      break;
    case 'thunder': {
      // A sharp crack, then a long rumble that rolls a few times.
      const big = 0.8 + Math.random() * 0.4;
      noiseEnv(0.35, 'highpass', 1400, [[0.01, 0.07 * big], [0.08, 0.03], [0.35, 0]]);
      noiseEnv(3.6, 'lowpass', 160 + Math.random() * 60, [[0.06, 0.5 * big], [0.5, 0.3], [0.9, 0.45 * big], [1.6, 0.2], [2.2, 0.28], [3.6, 0]]);
      break;
    }
    case 'quake':
      // Deep ground rumble, a low wobble, and things rattling on the shelves.
      noiseEnv(3.4, 'lowpass', 95, [[0.4, 0.55], [1.2, 0.75], [2.4, 0.6], [3.4, 0]]);
      tone(38, 3, 'sine', 0.09, 0.1, 30);
      for (let i = 0; i < 22; i++) noiseEnv(0.03, 'bandpass', 2400 + Math.random() * 2400, [[0.004, 0.05 + Math.random() * 0.05], [0.03, 0]], 0.3 + Math.random() * 2.6, 6);
      break;
    case 'stepHall':
      // Hard heel click on the corridor tiles, with two echoes down the hall.
      [0, 0.12, 0.25].forEach((at, i) => noiseEnv(0.05, 'bandpass', 2000 - i * 400, [[0.004, 0.09 / (1 + i * 2.2)], [0.05, 0]], at, 2.5));
      break;
    case 'stepWet':
      noiseEnv(0.09, 'bandpass', 1300 + Math.random() * 500, [[0.01, 0.07], [0.09, 0]], 0, 1.4);
      tone(900 + Math.random() * 300, 0.05, 'sine', 0.012, 0.02, 1500);
      break;
    case 'umbrella':
      noiseEnv(0.22, 'bandpass', 1800, [[0.12, 0.08], [0.22, 0]], 0, 0.7);
      tone(300, 0.06, 'triangle', 0.04, 0.2, 180);
      break;
    case 'alarm':
      // The phone's emergency alert (國家級警報) — two harsh alternating tones.
      for (let i = 0; i < 4; i++) {
        tone(853, 0.22, 'square', 0.035, i * 0.5);
        tone(960, 0.22, 'square', 0.035, i * 0.5 + 0.25);
      }
      break;
    case 'hop':
      tone(330, 0.16, 'square', 0.035, 0, 660);
      break;
    case 'rustle':
      for (let i = 0; i < 5; i++) noise(0.05, 0.05, 3000, i * 0.04);
      break;
    case 'line':
      [659.25, 880, 1174.7].forEach((f, i) => tone(f, 0.1, 'square', 0.035, i * 0.05));
      break;
  }
}

/** Instrumental birthday tune (traditional melody, public domain). */
export function playBirthdayTune(): void {
  const G = 392, A = 440, B = 493.88, C = 523.25, D = 587.33;
  const notes: [number, number, number][] = [
    [G, 0.25, 0], [G, 0.25, 0.35], [A, 0.55, 0.7], [G, 0.55, 1.3], [C, 0.55, 1.9], [B, 0.9, 2.5],
    [G, 0.25, 3.6], [G, 0.25, 3.95], [A, 0.55, 4.3], [G, 0.55, 4.9], [D, 0.55, 5.5], [C, 1.1, 6.1],
  ];
  for (const [f, d, t] of notes) {
    tone(f, d, 'triangle', 0.09, t);
    tone(f / 2, d, 'sine', 0.05, t);
  }
}

export const NOTE_FREQS: Record<string, number> = {
  C: 261.63, D: 293.66, E: 329.63, F: 349.23, G: 392.0, A: 440.0, B: 493.88, 'C²': 523.25,
};

export function pianoNote(freq: number): void {
  tone(freq, 0.9, 'triangle', 0.09);
  tone(freq * 2, 0.5, 'sine', 0.025);
}
